import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import BeautyPresetPreview from "../../components/BeautyPresetPreview";
import type { BeautyParameters, BeautyPresetWrite } from "../../services/beautyPresets";
import {
  BEAUTY_PARAMETER_LIMITS,
  BEAUTY_PRESET_LANGUAGES,
  DEFAULT_BEAUTY_PARAMETERS,
  createBeautyPreset,
  deleteBeautyPreset,
  getAdminBeautyPresetCatalog,
  reorderBeautyPresets,
  translateBeautyPresetName,
  updateBeautyPreset,
} from "../../services/beautyPresets";
import type { AdminBeautyPreset, AdminBeautyPresetCatalog } from "../../services/beautyPresets";

export type BeautyPresetLeaveHandler = () => Promise<boolean>;

export interface BeautyPresetsTabProps {
  onRegisterLeaveHandler?: (handler: BeautyPresetLeaveHandler | null) => void;
}

const LANGUAGE_LABELS: Record<string, string> = {
  zh: "中文",
  en: "English",
  ja: "日本語",
  ko: "한국어",
  de: "Deutsch",
  es: "Español",
  fr: "Français",
  pt: "Português",
  it: "Italiano",
  ru: "Русский",
};

type NumericParameterKey = Exclude<keyof BeautyParameters, "beautyMaskEnabled">;

const PARAMETER_FIELDS: Array<{ key: NumericParameterKey; label: string }> = [
  { key: "smoothing", label: "磨皮" },
  { key: "whiteness", label: "美白" },
  { key: "slimFace", label: "瘦脸" },
  { key: "bigEyes", label: "大眼" },
  { key: "blusher", label: "腮红" },
  { key: "lipstick", label: "口红" },
  { key: "exposure", label: "曝光" },
  { key: "contrast", label: "对比度" },
  { key: "saturation", label: "饱和度" },
];


function catalogError(): Error {
  return new Error("服务器返回的美颜预设目录格式无效，请刷新后重试");
}

function parseAdminCatalog(value: unknown): AdminBeautyPresetCatalog {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw catalogError();
  const envelope = value as Record<string, unknown>;
  if (envelope.schemaVersion !== 1 || !Number.isSafeInteger(envelope.revision) || Number(envelope.revision) < 0 || !Array.isArray(envelope.presets)) {
    throw catalogError();
  }

  const ids = new Set<string>();
  let defaultCount = 0;
  for (const itemValue of envelope.presets) {
    if (typeof itemValue !== "object" || itemValue === null || Array.isArray(itemValue)) throw catalogError();
    const item = itemValue as Record<string, unknown>;
    if (typeof item.id !== "string" || !item.id || ids.has(item.id) || typeof item.name !== "string" || !item.name.trim() || typeof item.enabled !== "boolean" || typeof item.isDefault !== "boolean" || (item.isDefault && !item.enabled) || typeof item.sortOrder !== "number" || !Number.isSafeInteger(item.sortOrder) || typeof item.i18n !== "object" || item.i18n === null || Array.isArray(item.i18n) || typeof item.parameters !== "object" || item.parameters === null || Array.isArray(item.parameters)) {
      throw catalogError();
    }
    const i18n = item.i18n as Record<string, unknown>;
    for (const languageValue of Object.values(i18n)) {
      if (typeof languageValue !== "object" || languageValue === null || Array.isArray(languageValue) || typeof (languageValue as Record<string, unknown>).name !== "string") throw catalogError();
    }
    const parameters = item.parameters as Record<string, unknown>;
    for (const key of Object.keys(BEAUTY_PARAMETER_LIMITS) as NumericParameterKey[]) {
      const parameter = parameters[key];
      const [min, max] = BEAUTY_PARAMETER_LIMITS[key];
      if (typeof parameter !== "number" || !Number.isFinite(parameter) || parameter < min || parameter > max) throw catalogError();
    }
    if (typeof parameters.beautyMaskEnabled !== "boolean") throw catalogError();
    if (item.isDefault) defaultCount += 1;
    if (defaultCount > 1) throw catalogError();
    ids.add(item.id);
  }
  return value as unknown as AdminBeautyPresetCatalog;
}

function orderedRows(rows: AdminBeautyPreset[]): AdminBeautyPreset[] {
  return [...rows].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
}

function cloneParameters(parameters: BeautyParameters): BeautyParameters {
  return { ...parameters };
}

function cloneI18n(i18n: Record<string, { name: string }>): Record<string, { name: string }> {
  return Object.fromEntries(
    Object.entries(i18n || {}).map(([language, value]) => [language, { name: typeof value?.name === "string" ? value.name : "" }]),
  );
}

function cloneDraft(draft: BeautyPresetWrite): BeautyPresetWrite {
  return {
    name: draft.name,
    i18n: cloneI18n(draft.i18n),
    parameters: cloneParameters(draft.parameters),
    enabled: draft.enabled,
    isDefault: draft.isDefault,
  };
}

function newDraft(): BeautyPresetWrite {
  return {
    name: "",
    i18n: Object.fromEntries(BEAUTY_PRESET_LANGUAGES.map((language) => [language, { name: "" }])),
    parameters: cloneParameters(DEFAULT_BEAUTY_PARAMETERS),
    enabled: false,
    isDefault: false,
  };
}

function draftFromRow(row: AdminBeautyPreset): BeautyPresetWrite {
  const i18n = cloneI18n(row.i18n);
  for (const language of BEAUTY_PRESET_LANGUAGES) {
    i18n[language] ??= { name: "" };
  }
  return {
    name: row.name,
    i18n,
    parameters: cloneParameters(row.parameters),
    enabled: row.enabled,
    isDefault: row.isDefault,
  };
}

function normalizedDraft(draft: BeautyPresetWrite): BeautyPresetWrite {
  const i18n = cloneI18n(draft.i18n);
  for (const language of BEAUTY_PRESET_LANGUAGES) {
    i18n[language] ??= { name: "" };
  }
  return {
    name: draft.name.trim(),
    i18n: Object.fromEntries(Object.entries(i18n).map(([language, value]) => [language, { name: value.name.trim() }])),
    parameters: cloneParameters(draft.parameters),
    enabled: draft.enabled,
    isDefault: draft.enabled && draft.isDefault,
  };
}

function validateDraft(draft: BeautyPresetWrite): string | null {
  if (!draft.name.trim()) return "请填写中文基础名称";
  if (draft.isDefault && !draft.enabled) return "停用的预设不能设为默认";
  if (typeof draft.i18n !== "object" || draft.i18n === null || Array.isArray(draft.i18n)) return "多语言名称数据无效";
  for (const language of BEAUTY_PRESET_LANGUAGES) {
    const languageValue = draft.i18n[language];
    if (typeof languageValue !== "object" || languageValue === null || Array.isArray(languageValue) || typeof languageValue.name !== "string") {
      return `请补全${LANGUAGE_LABELS[language]}名称字段`;
    }
  }
  if (typeof draft.parameters !== "object" || draft.parameters === null || Array.isArray(draft.parameters)) return "美颜参数数据无效";
  for (const { key, label } of PARAMETER_FIELDS) {
    const value = draft.parameters[key];
    const [min, max] = BEAUTY_PARAMETER_LIMITS[key];
    if (typeof value !== "number" || !Number.isFinite(value) || value < min || value > max) {
      return `${label}必须是 ${min} 至 ${max} 之间的有效数字`;
    }
  }
  if (typeof draft.parameters.beautyMaskEnabled !== "boolean") return "主人物遮罩开关数据无效";
  return null;
}

function errorMessage(reason: unknown): string {
  return reason instanceof Error && reason.message ? reason.message : "未知错误";
}

function displayName(row: AdminBeautyPreset): string {

  return row.i18n?.zh?.name?.trim() || row.name;
}

type PendingAction = {
  summary: string;
  action: () => void | Promise<void>;
};
type NameTranslationRequest = {
  source: string;
  langs: string[];
};

const TRANSLATION_LANGUAGES = BEAUTY_PRESET_LANGUAGES.filter((language) => language !== "zh");


function BeautyPresetsTab({ onRegisterLeaveHandler }: BeautyPresetsTabProps) {
  const [rows, setRows] = useState<AdminBeautyPreset[]>([]);
  const rowsRef = useRef<AdminBeautyPreset[]>([]);
  const selectedIdRef = useRef<string | null>(null);
  const loadAbortRef = useRef<AbortController | null>(null);
  const mutationRef = useRef(false);
  const pendingLeaveResolveRef = useRef<((allowed: boolean) => void) | null>(null);
  const pendingLeavePromiseRef = useRef<Promise<boolean> | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<BeautyPresetWrite | null>(null);
  const [baseline, setBaseline] = useState<BeautyPresetWrite | null>(null);
  const [revision, setRevision] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [languageTab, setLanguageTab] = useState<string>("zh");
  const [unsavedPrompt, setUnsavedPrompt] = useState<PendingAction | null>(null);
  const translationRequestRef = useRef<NameTranslationRequest | null>(null);
  const translationInFlightRef = useRef(false);
  const dirty = Boolean(draft && baseline && JSON.stringify(draft) !== JSON.stringify(baseline));
  const blocker = useBlocker(dirty);
  const ordered = useMemo(() => orderedRows(rows), [rows]);
  const invalidateTranslation = useCallback((updateState = true) => {
    translationRequestRef.current = null;
    translationInFlightRef.current = false;
    if (updateState) setTranslating(false);
  }, []);

  const installDraft = useCallback((nextDraft: BeautyPresetWrite) => {
    invalidateTranslation();
    setDraft(nextDraft);
    setBaseline(cloneDraft(nextDraft));
  }, [invalidateTranslation]);

  const resolvePendingLeave = useCallback((allowed: boolean) => {
    const resolve = pendingLeaveResolveRef.current;
    pendingLeaveResolveRef.current = null;
    resolve?.(allowed);
  }, []);

  const requestLeave = useCallback((): Promise<boolean> => {
    invalidateTranslation();
    if (!dirty) return Promise.resolve(true);
    if (pendingLeavePromiseRef.current) return pendingLeavePromiseRef.current;
    let resolveRequest!: (allowed: boolean) => void;
    const promise = new Promise<boolean>((resolve) => {
      resolveRequest = resolve;
    });
    pendingLeaveResolveRef.current = resolveRequest;
    pendingLeavePromiseRef.current = promise;
    setUnsavedPrompt({
      summary: "切换 AUGC 标签页",
      action: () => resolvePendingLeave(true),
    });
    void promise.then(
      () => {
        if (pendingLeavePromiseRef.current === promise) pendingLeavePromiseRef.current = null;
      },
      () => {
        if (pendingLeavePromiseRef.current === promise) pendingLeavePromiseRef.current = null;
      },
    );
    return promise;
  }, [dirty, invalidateTranslation, resolvePendingLeave]);


  const applyCatalog = useCallback((catalog: AdminBeautyPresetCatalog, preferredId?: string | null) => {
    const nextRows = orderedRows(catalog.presets);
    rowsRef.current = nextRows;
    setRows(nextRows);
    setRevision(catalog.revision);

    let nextId = preferredId === undefined ? selectedIdRef.current : preferredId;
    if (nextId && !nextRows.some((row) => row.id === nextId)) nextId = null;
    if (!nextId && nextRows.length > 0) nextId = nextRows[0].id;

    selectedIdRef.current = nextId;
    setSelectedId(nextId);
    if (nextId) {
      const row = nextRows.find((candidate) => candidate.id === nextId);
      if (row) installDraft(draftFromRow(row));
    } else {
      installDraft(newDraft());
    }
  }, [installDraft]);

  const loadCatalog = useCallback(async () => {
    invalidateTranslation();
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setLoading(true);
    setLoadError(null);
    setError(null);
    try {
      const response = await getAdminBeautyPresetCatalog(controller.signal);
      if (controller.signal.aborted) return;
      applyCatalog(parseAdminCatalog(response));
    } catch (reason) {
      if (controller.signal.aborted) return;
      setLoadError(`加载美颜预设失败：${errorMessage(reason)}`);
    } finally {
      if (loadAbortRef.current === controller) {
        setLoading(false);
        loadAbortRef.current = null;
      }
    }
  }, [applyCatalog, invalidateTranslation]);

  useEffect(() => {
    void loadCatalog();
    return () => loadAbortRef.current?.abort();
  }, [loadCatalog]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);
  useEffect(() => {
    if (blocker.state !== "blocked") return undefined;
    invalidateTranslation();
    const prompt: PendingAction = {
      summary: "离开美颜预设页面",
      action: () => blocker.proceed(),
    };
    setUnsavedPrompt(prompt);
    return undefined;
  }, [blocker.state, invalidateTranslation]);
  useEffect(() => {
    if (!onRegisterLeaveHandler) return undefined;
    onRegisterLeaveHandler(requestLeave);
    return () => onRegisterLeaveHandler(null);
  }, [onRegisterLeaveHandler, requestLeave]);

  useEffect(() => () => {
    invalidateTranslation(false);
    resolvePendingLeave(false);
  }, [invalidateTranslation, resolvePendingLeave]);

  const selectPreset = (id: string) => {
    const row = rowsRef.current.find((candidate) => candidate.id === id);
    if (!row) return;
    selectedIdRef.current = id;
    setSelectedId(id);
    installDraft(draftFromRow(row));
    setError(null);
    setNotice(null);
  };

  const startNew = () => {
    selectedIdRef.current = null;
    setSelectedId(null);
    installDraft(newDraft());
    setError(null);
    setNotice(null);
    setLanguageTab("zh");
  };

  const revertDraft = () => {
    if (!baseline) return;
    installDraft(cloneDraft(baseline));
    setError(null);
    setNotice(null);
  };

  const requestAction = (summary: string, action: PendingAction["action"]) => {
    if (mutationRef.current) return;
    invalidateTranslation();
    if (dirty) {
      setUnsavedPrompt({ summary, action });
      return;
    }
    void action();
  };

  const sendCatalogMutation = async (
    label: string,
    operation: () => Promise<AdminBeautyPresetCatalog>,
  ): Promise<AdminBeautyPresetCatalog | null> => {
    if (mutationRef.current) return null;
    invalidateTranslation();
    mutationRef.current = true;
    setBusy(label);
    setError(null);
    setNotice(null);
    try {
      return parseAdminCatalog(await operation());
    } catch (reason) {
      setError(`${label}失败：${errorMessage(reason)}`);
      return null;
    } finally {
      mutationRef.current = false;
      setBusy(null);
    }
  };

  const saveDraft = async (): Promise<boolean> => {
    invalidateTranslation();
    if (!draft) return false;
    const validation = validateDraft(draft);
    if (validation) {
      setError(validation);
      return false;
    }
    const body = normalizedDraft(draft);
    const editingId = selectedIdRef.current;
    const response = editingId
      ? await sendCatalogMutation("保存预设", () => updateBeautyPreset(editingId, body))
      : await sendCatalogMutation("创建预设", () => createBeautyPreset(body));
    if (!response) return false;

    let preferredId = editingId;
    if (!preferredId) {
      const candidates = response.presets.filter((row) => row.name === body.name);
      preferredId = orderedRows(candidates).at(-1)?.id ?? orderedRows(response.presets).at(-1)?.id ?? null;
    }
    applyCatalog(response, preferredId);
    setNotice("美颜预设已保存");
    return true;
  };

  const runSetEnabled = async (id: string, enabled: boolean) => {
    const row = rowsRef.current.find((candidate) => candidate.id === id);
    if (!row) return;
    const response = await sendCatalogMutation(enabled ? "启用预设" : "停用预设", () => updateBeautyPreset(id, { enabled }));
    if (response) {
      applyCatalog(response);
      setNotice(enabled ? "预设已启用" : "预设已停用；如它是默认预设，默认已清除");
    }
  };

  const runSetDefault = async (id: string, isDefault: boolean) => {
    const row = rowsRef.current.find((candidate) => candidate.id === id);
    if (!row || (isDefault && !row.enabled)) return;
    const response = await sendCatalogMutation(isDefault ? "设为默认" : "取消默认", () => updateBeautyPreset(id, { isDefault }));
    if (response) {
      applyCatalog(response);
      setNotice(isDefault ? "默认预设已更新" : "已取消默认预设，当前将使用原图");
    }
  };

  const runMove = async (id: string, offset: -1 | 1) => {
    const current = orderedRows(rowsRef.current);
    const index = current.findIndex((row) => row.id === id);
    const target = index + offset;
    if (index < 0 || target < 0 || target >= current.length) return;
    const ids = current.map((row) => row.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    const response = await sendCatalogMutation("保存预设顺序", () => reorderBeautyPresets(ids));
    if (response) {
      applyCatalog(response);
      setNotice("预设顺序已保存");
    }
  };

  const runDelete = async (id: string) => {
    const row = rowsRef.current.find((candidate) => candidate.id === id);
    if (!row || !window.confirm(`确认删除「${displayName(row)}」？删除后不可恢复。`)) return;
    const response = await sendCatalogMutation("删除预设", () => deleteBeautyPreset(id));
    if (response) {
      applyCatalog(response);
      setNotice("美颜预设已删除；如它是默认预设，默认已清除");
    }
  };

  const updateParameter = (key: NumericParameterKey, value: number) => {
    setDraft((current) => current ? { ...current, parameters: { ...current.parameters, [key]: value } } : current);
    setError(null);
    setNotice(null);
  };

  const updateBaseName = (name: string) => {
    invalidateTranslation();
    setDraft((current) => current ? { ...current, name } : current);
    setError(null);
    setNotice(null);
  };

  const updateLanguageName = (language: string, name: string) => {
    setDraft((current) => current ? { ...current, i18n: { ...current.i18n, [language]: { name } } } : current);
    setError(null);
    setNotice(null);
  };

  const startNameTranslation = () => {
    const currentDraft = draft;
    if (!currentDraft || translationInFlightRef.current || loading || Boolean(busy)) return;
    const source = currentDraft.name.trim();
    if (!source) return;
    const langs = TRANSLATION_LANGUAGES;

    const request: NameTranslationRequest = {
      source,
      langs,
    };
    translationRequestRef.current = request;
    translationInFlightRef.current = true;
    setTranslating(true);
    setError(null);
    setNotice(null);

    void translateBeautyPresetName(request.source, request.langs)
      .then((response) => {
        if (translationRequestRef.current !== request) return;
        setDraft((current) => {
          if (!current || translationRequestRef.current !== request) return current;

          return { ...current, i18n: { ...current.i18n, ...response.i18n, zh: { name: request.source } } };
        });
        if (translationRequestRef.current === request) {
          setNotice("全部语言名称已更新到草稿，请检查后保存");
        }
      })
      .catch((reason) => {
        if (translationRequestRef.current !== request) return;
        setError(`外语翻译失败：${errorMessage(reason)}`);
      })
      .finally(() => {
        if (translationRequestRef.current !== request) return;
        translationInFlightRef.current = false;
        setTranslating(false);
      });
  };

  const handleSaveAndContinue = async () => {
    if (!unsavedPrompt || mutationRef.current) return;
    const pending = unsavedPrompt;
    const saved = await saveDraft();
    if (!saved) return;
    setUnsavedPrompt(null);
    await pending.action();
  };

  const handleDiscardAndContinue = async () => {
    if (!unsavedPrompt) return;
    const pending = unsavedPrompt;
    setUnsavedPrompt(null);
    revertDraft();
    await pending.action();
  };
  const cancelUnsaved = () => {
    if (blocker.state === "blocked") blocker.reset();
    resolvePendingLeave(false);
    setUnsavedPrompt(null);
  };

  const previewParameters = draft?.parameters ?? DEFAULT_BEAUTY_PARAMETERS;
  const catalogControls = (
    <div className="flex items-center gap-2">
      {revision !== null && <span className="text-xs text-gray-400">目录修订 {revision}</span>}
      <button
        type="button"
        onClick={() => requestAction("刷新美颜预设目录", () => loadCatalog())}
        disabled={loading || Boolean(busy)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} /> 刷新
      </button>
    </div>
  );
  const selectedRow = selectedId ? rows.find((row) => row.id === selectedId) : null;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">{catalogControls}</div>

      {loadError && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          <span><i className="ri-error-warning-line mr-1.5" />{loadError}</span>
          <button type="button" onClick={() => requestAction("刷新美颜预设目录", () => loadCatalog())} className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium hover:bg-red-100">重新加载</button>
        </div>
      )}
      {error && !loadError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          <i className="ri-error-warning-line mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {notice && !error && <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-700"><i className="ri-check-line mr-1.5" />{notice}</div>}
      {busy && <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800"><i className="ri-loader-4-line animate-spin" />{busy}，请稍候…</div>}

      <div className="grid min-h-[680px] grid-cols-1 gap-4 xl:grid-cols-[250px_minmax(360px,1fr)_390px]">
        <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" aria-label="预设列表">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">预设列表</h2>
              <p className="mt-0.5 text-xs text-gray-400">共 {rows.length} 个（含停用）</p>
            </div>
            <button
              type="button"
              onClick={() => requestAction("新建美颜预设", startNew)}
              disabled={loading || Boolean(busy) || Boolean(loadError)}
              className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-add-line" /> 新建
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {loading && <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400"><i className="ri-loader-4-line animate-spin" />加载中…</div>}
            {!loading && !loadError && ordered.length === 0 && (
              <div className="py-12 text-center text-sm text-gray-400">
                <i className="ri-magic-line mb-2 block text-3xl text-gray-300" />
                <p>暂无云端预设</p>
                <p className="mt-1 text-xs">点击“新建”添加第一个效果</p>
              </div>
            )}
            {!loading && ordered.map((row, index) => {
              const active = selectedId === row.id;
              return (
                <div key={row.id} className={`mb-1 rounded-lg border p-2.5 transition-colors ${active ? "border-teal-300 bg-teal-50/70" : "border-transparent hover:border-gray-200 hover:bg-gray-50"}`}>
                  <button
                    type="button"
                    onClick={() => requestAction("切换编辑中的美颜预设", () => selectPreset(row.id))}
                    className="w-full text-left"
                    disabled={Boolean(busy)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className={`min-w-0 truncate text-sm font-medium ${row.enabled ? "text-gray-900" : "text-gray-400 line-through"}`}>{displayName(row)}</span>
                      {row.isDefault && <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">默认</span>}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                      <span className={`rounded px-1.5 py-0.5 ${row.enabled ? "bg-teal-100 text-teal-700" : "bg-gray-100 text-gray-500"}`}>{row.enabled ? "启用" : "停用"}</span>
                      <span className="text-gray-400">顺序 {index + 1}</span>
                    </div>
                  </button>
                  <div className="mt-2 flex items-center justify-end gap-1 border-t border-gray-100 pt-2">
                    <button type="button" title={row.enabled ? "停用" : "启用"} aria-label={`${row.enabled ? "停用" : "启用"}${displayName(row)}`} onClick={() => requestAction(row.enabled ? "停用美颜预设" : "启用美颜预设", () => runSetEnabled(row.id, !row.enabled))} disabled={Boolean(busy)} className={`flex h-7 w-7 items-center justify-center rounded text-xs ${row.enabled ? "text-teal-600 hover:bg-teal-100" : "text-gray-400 hover:bg-gray-100"}`}>
                      <i className={row.enabled ? "ri-eye-line" : "ri-eye-off-line"} />
                    </button>
                    <button type="button" title={row.isDefault ? "取消默认" : "设为默认"} aria-label={`${row.isDefault ? "取消默认" : "设为默认"}${displayName(row)}`} onClick={() => requestAction(row.isDefault ? "取消默认预设" : "设置默认预设", () => runSetDefault(row.id, !row.isDefault))} disabled={Boolean(busy) || !row.enabled} className={`flex h-7 w-7 items-center justify-center rounded text-xs ${row.isDefault ? "text-amber-600 hover:bg-amber-100" : "text-gray-400 hover:bg-gray-100 disabled:text-gray-200"}`}>
                      <i className="ri-star-line" />
                    </button>
                    <button type="button" title="上移" aria-label={`上移${displayName(row)}`} onClick={() => requestAction("调整美颜预设顺序", () => runMove(row.id, -1))} disabled={Boolean(busy) || index === 0} className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 disabled:text-gray-200"><i className="ri-arrow-up-line" /></button>
                    <button type="button" title="下移" aria-label={`下移${displayName(row)}`} onClick={() => requestAction("调整美颜预设顺序", () => runMove(row.id, 1))} disabled={Boolean(busy) || index === ordered.length - 1} className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 disabled:text-gray-200"><i className="ri-arrow-down-line" /></button>
                    <button type="button" title="删除" aria-label={`删除${displayName(row)}`} onClick={() => requestAction("删除美颜预设", () => runDelete(row.id))} disabled={Boolean(busy)} className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:text-gray-200"><i className="ri-delete-bin-line" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" aria-label="美颜效果预览">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">效果预览</h2>
              <p className="mt-0.5 text-xs text-gray-400">拖动右侧参数即可实时查看</p>
            </div>
            <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] text-gray-500">本机测试照片</span>
          </div>
          <div className="min-h-0 flex-1 bg-gray-950/95 p-3 sm:p-5">
            <BeautyPresetPreview parameters={previewParameters} label={draft?.name.trim() || undefined} />
          </div>
          <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-xs leading-relaxed text-gray-500">
            预览引擎使用与 App 一致的十项参数。测试照片只保留在当前浏览器内，保存预设时不会上传图片。
          </div>
        </section>

        <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" aria-label="预设编辑器">
          <div className="border-b border-gray-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">{selectedRow ? `编辑：${displayName(selectedRow)}` : "新建美颜预设"}</h2>
            <p className="mt-0.5 text-xs text-gray-400">中文为基础名称，其他语言用于 App 展示</p>
          </div>
          {!draft ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-sm text-gray-400">
              <i className="ri-edit-box-line text-3xl text-gray-300" />
              <p>{loadError ? "目录加载失败，暂不能编辑" : "请选择预设或点击新建"}</p>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                <div>
                  <div className="mb-1.5 flex flex-wrap items-center gap-2">
                    <label htmlFor="beauty-base-name" className="text-xs font-medium text-gray-700">中文基础名称 <span className="text-red-500">*</span></label>
                    <button
                      type="button"
                      onClick={startNameTranslation}
                      disabled={!draft.name.trim() || loading || Boolean(busy) || translating || Boolean(loadError)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-2 py-1 text-[11px] font-medium text-teal-700 hover:bg-teal-100 disabled:cursor-not-allowed disabled:opacity-40"
                      title="翻译成功后统一填入：中文复制基础名，其他语言覆盖译名；仅更新草稿，需要保存"
                    >
                      <i className={translating ? "ri-loader-4-line animate-spin" : "ri-translate-2"} />
                      {translating ? "翻译中…" : "自动翻译"}
                    </button>
                  </div>
                  <input id="beauty-base-name" type="text" value={draft.name} disabled={translating} onChange={(event) => updateBaseName(event.target.value)} placeholder="例如：自然" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 disabled:cursor-not-allowed disabled:opacity-60" />
                  <p className="mt-1 text-[11px] text-gray-400">{translating ? "翻译中，名称暂不可编辑；成功后统一填入草稿。" : "翻译成功后，中文与其他语言统一填入草稿；请检查后点击“保存”。"}</p>
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between"><span className="text-xs font-medium text-gray-700">多语言名称</span><span className="text-[11px] text-gray-400">可留空，运行时回退中文</span></div>
                  <div className="grid grid-cols-5 gap-1 rounded-lg bg-gray-100 p-1">
                    {BEAUTY_PRESET_LANGUAGES.map((language) => (
                      <button key={language} type="button" onClick={() => setLanguageTab(language)} className={`rounded-md px-1 py-1.5 text-[11px] transition-colors ${languageTab === language ? "bg-white font-semibold text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}>{LANGUAGE_LABELS[language]}</button>
                    ))}
                  </div>
                  <input type="text" value={draft.i18n[languageTab]?.name ?? ""} disabled={translating} onChange={(event) => updateLanguageName(languageTab, event.target.value)} placeholder={`${LANGUAGE_LABELS[languageTab]}名称`} className="mt-2 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 disabled:cursor-not-allowed disabled:opacity-60" />
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between"><span className="text-xs font-medium text-gray-700">美颜参数</span><span className="text-[11px] text-gray-400">数值实时预览</span></div>
                  <div className="space-y-3 rounded-lg border border-gray-100 bg-gray-50/70 p-3">
                    {PARAMETER_FIELDS.map(({ key, label }) => {
                      const [min, max] = BEAUTY_PARAMETER_LIMITS[key];
                      const value = draft.parameters[key];
                      return (
                        <div key={key} className="grid grid-cols-[62px_minmax(0,1fr)_58px] items-center gap-2">
                          <label htmlFor={`beauty-${key}`} className="text-xs text-gray-600">{label}</label>
                          <input id={`beauty-${key}`} type="range" min={min} max={max} step="1" value={value} onChange={(event) => updateParameter(key, Number(event.target.value))} className="accent-teal-600" />
                          <input type="number" min={min} max={max} step="1" value={value} aria-label={`${label}数值`} onChange={(event) => updateParameter(key, Number(event.target.value))} className="w-full rounded-md border border-gray-200 bg-white px-1.5 py-1 text-right text-xs focus:border-teal-500 focus:outline-none" />
                        </div>
                      );
                    })}
                    <label className="flex cursor-pointer items-center justify-between border-t border-gray-200 pt-3 text-xs text-gray-700">
                      <span>主人物皮肤遮罩</span>
                      <input type="checkbox" checked={draft.parameters.beautyMaskEnabled} onChange={(event) => { setDraft((current) => current ? { ...current, parameters: { ...current.parameters, beautyMaskEnabled: event.target.checked } } : current); setError(null); setNotice(null); }} className="h-4 w-4 accent-teal-600" />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 rounded-lg border border-gray-100 bg-gray-50/70 p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-700"><input type="checkbox" checked={draft.enabled} onChange={(event) => { const enabled = event.target.checked; setDraft((current) => current ? { ...current, enabled, isDefault: enabled ? current.isDefault : false } : current); setError(null); setNotice(null); }} className="h-4 w-4 accent-teal-600" />启用预设</label>
                  <label className={`flex items-center gap-2 text-xs ${draft.enabled ? "cursor-pointer text-gray-700" : "text-gray-300"}`}><input type="checkbox" checked={draft.isDefault} disabled={!draft.enabled} onChange={(event) => { setDraft((current) => current ? { ...current, isDefault: event.target.checked } : current); setError(null); setNotice(null); }} className="h-4 w-4 accent-amber-500" />设为默认</label>
                </div>
                {!draft.enabled && <p className="-mt-2 text-[11px] text-gray-400">停用预设不会出现在 App；停用默认预设后将使用原图，不会自动指定其他预设。</p>}
              </div>
            </div>
          )}
          {draft && (
            <div className="flex items-center justify-between gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3">
              <span className={`text-xs ${dirty ? "text-amber-600" : "text-gray-400"}`}>{dirty ? "有未保存修改" : "已保存"}</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={revertDraft} disabled={!dirty || Boolean(busy)} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40">撤销</button>
                <button type="button" onClick={() => void saveDraft()} disabled={Boolean(busy)} className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-xs font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"><i className="ri-save-line" />保存</button>
              </div>
            </div>
          )}
        </section>
      </div>

      {unsavedPrompt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="beauty-unsaved-title">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700"><i className="ri-error-warning-line" /></div>
              <div>
                <h2 id="beauty-unsaved-title" className="text-base font-semibold text-gray-900">有未保存修改</h2>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">{unsavedPrompt.summary}前，请先处理当前草稿。选择保存会先提交当前编辑内容。</p>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button type="button" onClick={cancelUnsaved} disabled={Boolean(busy)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">取消</button>
              <button type="button" onClick={() => void handleDiscardAndContinue()} disabled={Boolean(busy)} className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50">放弃修改并继续</button>
              <button type="button" onClick={() => void handleSaveAndContinue()} disabled={Boolean(busy)} className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50">保存并继续</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default BeautyPresetsTab;
